import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Box,
  styled,
  CircularProgress,
  useTheme,
  Button,
  ButtonGroup,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  useMediaQuery
} from '@mui/material';
import GridLayout, { Layout } from 'react-grid-layout';

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import TuneIcon from '@mui/icons-material/Tune';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';

import { auth } from '../../../firebase';
import CurrencyService from '../../../services/CurrencyService';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import firebaseDataService from '../../../services/FirebaseDataService';
import { taskDataService } from '../../../services/taskDataService';
import salesService, { ScreenTopSellingProduct } from '../../../services/salesService';
import aiChatService from '../../../services/aiChatService';
import CashboxListWidget from './components/CashboxListWidget';
import BankAccountListWidget from './components/BankAccountListWidget';
import CurrentAccountListWidget from './components/CurrentAccountListWidget';
import BestSellingProductsWidget from './components/BestSellingProductsWidget';
import TopSellingProductsModal from './components/TopSellingProductsModal';
import DashboardSettingsModal from './components/DashboardSettingsModal';
import ScreenSelectDialog, { SelectedScreen } from './components/ScreenSelectDialog';
import ReportBuilderDialog from './components/ReportBuilderDialog';
import AiReportDialog from './components/AiReportDialog';
import GuideButton from '../../common/guide/GuideButton';
import DynamicReportCard from './components/DynamicReportCard';
import { CUSTOM_REPORT_ID_PREFIX, ReportSpec, verifyReportCatalogAgainstBackend } from './reportCatalog';
import { deleteCustomReport, loadCustomReports, upsertCustomReport } from './customReportsStore';
import { useDashboardLayout, ExtraCardDef, buildDefaultLayoutWithExtras } from './useDashboardLayout';
import {
  DASHBOARD_CARDS,
  DASHBOARD_GRID_COLS,
  DASHBOARD_ROW_HEIGHT,
  DASHBOARD_GRID_MARGIN,
} from './dashboardCardsConfig';
import SalesDistributionChart from './charts/SalesDistributionChart';
import BestSellingProductsChart from './charts/BestSellingProductsChart';
import CashboxBalanceChart from './charts/CashboxBalanceChart';
import BankBalanceChart from './charts/BankBalanceChart';

// Removed ScrollableContent - MainLayout's ContentContainer already handles scrolling

const StatsCard = styled(Paper)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
    background: `linear-gradient(to right, white, ${bgcolor}10)`,
  },
}));

const IconBox = styled(Box)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgcolor,
  marginRight: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    color: 'white',
    fontSize: 24,
  },
}));

const StatsText = styled(Box)({
  flexGrow: 1,
  '& .MuiTypography-h6': {
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  '& .MuiTypography-body2': {
    fontSize: '0.75rem',
  },
});

// Kasa arayüzü
interface CashboxItem {
  id: string;
  name: string;
  description: string;
  currency: string;
  balance: number;
  createdAt: string;
  isActive: boolean;
  branchId?: string | null;
  companyBranchName?: string | null;
  isDefault?: boolean;
  hasTransactions?: boolean;
}

// Banka hesabı arayüzü
interface BankAccount {
  id: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  isActive: boolean;
  branchId?: string | null;
  companyBranchName?: string | null;
  subAccounts?: { [key: string]: SubAccount };
}

// Alt hesap arayüzü
interface SubAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  isActive: boolean;
  parentId: string;
}

// Cari hesap arayüzü
interface CurrentAccount {
  id: string;
  name: string;
  accountType: string;
  balance: number;
  debitBalance: number; // ✅ Borç bakiyesi
  creditBalance: number; // ✅ Alacak bakiyesi
  currency: string;
  createdAt: string;
  isActive: boolean;
  transactionOrder?: number; // ✅ İşlem sırası (son işlem yapılanlar için)
}

// En çok satan ürün arayüzü
interface ProductSales {
  itemId: string;
  itemName: string;
  totalSales: number;
}

// ✅ Ekran bazlı kart takımları: seçili ekranlar (cihaz bazlı, localStorage)
const SCREEN_SELECTION_STORAGE_KEY = 'dashboard_screen_selection_v1';

type ScreenSelectionKind = 'quickSale' | 'restaurant' | 'package';

interface ScreenSelectionStorage {
  /** Hızlı satış ekranı seçimi (id + ad) */
  quickSale?: Array<{ id: number; name: string }>;
  /** Restoran ekranı seçimi (id + ad) */
  restaurant?: Array<{ id: number; name: string }>;
  /** Paket sipariş ekranı seçimi (id + ad) */
  package?: Array<{ id: number; name: string }>;
}

const loadScreenSelection = (kind: ScreenSelectionKind): Array<{ id: number; name: string }> => {
  try {
    const raw = localStorage.getItem(SCREEN_SELECTION_STORAGE_KEY);
    const parsed: ScreenSelectionStorage = raw ? JSON.parse(raw) : {};
    const list = parsed[kind];
    if (!Array.isArray(list)) return [];
    return list.filter(
      (s): s is { id: number; name: string } =>
        !!s && typeof s.id === 'number' && typeof s.name === 'string'
    );
  } catch {
    return [];
  }
};

const saveScreenSelection = (kind: ScreenSelectionKind, screens: Array<{ id: number; name: string }>) => {
  try {
    const raw = localStorage.getItem(SCREEN_SELECTION_STORAGE_KEY);
    const parsed: ScreenSelectionStorage = raw ? JSON.parse(raw) : {};
    parsed[kind] = screens;
    localStorage.setItem(SCREEN_SELECTION_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    /* storage dolu olabilir - sessiz geç */
  }
};

// Theme colors
const THEME_COLORS = {
  cari: '#1976D2',      // Blue
  stok: '#9C27B0',      // Purple
  banka: '#FF9800',     // Orange
  kasa: '#4CAF50',      // Green
  depo: '#E91E63',      // Pink
  doviz: '#00BCD4',     // Cyan
  personnel: '#1976D2', // Mavi
  tasks: '#9C27B0'      // Mor
};

// ✅ Şube bazlı satış özeti tipi (server-side agregasyon sonucu)
interface BranchSalesSummary {
  branchId: string | null;
  totalBalance: number;
  transactionCount: number;
}

// ✅ Props arayüzü - Manager ve şube filtresi desteği
interface AccountingDashboardProps {
  isManager?: boolean;
  branchId?: string | null;
}

const AccountingDashboard: React.FC<AccountingDashboardProps> = ({ isManager = false, branchId: propBranchId = null }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';
  const dateFnsLocale = i18n.resolvedLanguage?.startsWith('en') ? enLocale : trLocale;
  const { userDetails, warehouses, branches } = useAuth();
  // ✅ Mobilde grid düzenleme kapalı; kartlar alt alta dizilir
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  
  // ✅ Şube filtresi state'i - Manager ise kilitli, normal kullanıcı ise seçilebilir
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(propBranchId);
  
  const [inventoryItemCount, setInventoryItemCount] = useState<number>(0);
  const [totalBankBalance, setTotalBankBalance] = useState<number>(0);
  const [totalCashboxBalance, setTotalCashboxBalance] = useState<number>(0);
  const [warehouseCount, setWarehouseCount] = useState<number>(0);
  const [totalPersonnel, setTotalPersonnel] = useState<number>(0); // ✅ YENİ
  const [activeTasks, setActiveTasks] = useState<number>(0); // ✅ YENİ
  const [cashboxes, setCashboxes] = useState<CashboxItem[]>([]); // ✅ YENİ: Kasalar
  const [loadingCashboxes, setLoadingCashboxes] = useState<boolean>(true); // ✅ YENİ
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]); // ✅ YENİ: Banka Hesapları
  const [loadingBankAccounts, setLoadingBankAccounts] = useState<boolean>(true); // ✅ YENİ
  // ✅ Şube bazlı satış özeti (seçili dönem)
  const [branchSummaries, setBranchSummaries] = useState<BranchSalesSummary[]>([]);
  const [loadingBranchSales, setLoadingBranchSales] = useState<boolean>(true);
  const [bestSellingProducts, setBestSellingProducts] = useState<ProductSales[]>([]); // ✅ YENİ: En çok satan ürünler
  const [loadingBestSelling, setLoadingBestSelling] = useState<boolean>(true); // ✅ YENİ
  const [topSellingModalOpen, setTopSellingModalOpen] = useState(false); // ✅ YENİ: En çok satanlar detay modalı

  // ✅ YENİ: Ekran bazlı en çok satanlar (hızlı satış ekranı seçimli 3'lü kart takımı)
  const [selectedSaleScreens, setSelectedSaleScreens] = useState<SelectedScreen[]>(() => loadScreenSelection('quickSale'));
  const [screenSelectOpen, setScreenSelectOpen] = useState(false);
  const [screenTopProducts, setScreenTopProducts] = useState<ScreenTopSellingProduct[]>([]);
  const [screenTopTotals, setScreenTopTotals] = useState<{ totalAmount: number; totalQuantity: number }>({ totalAmount: 0, totalQuantity: 0 });
  const [loadingScreenTop, setLoadingScreenTop] = useState(false);

  // ✅ YENİ: Restoran ekranı bazlı en çok satanlar (restoran ekranı seçimli 3'lü kart takımı)
  const [selectedRestaurantScreens, setSelectedRestaurantScreens] = useState<SelectedScreen[]>(() => loadScreenSelection('restaurant'));
  const [restaurantScreenSelectOpen, setRestaurantScreenSelectOpen] = useState(false);
  const [restaurantScreenTopProducts, setRestaurantScreenTopProducts] = useState<ScreenTopSellingProduct[]>([]);
  const [restaurantScreenTopTotals, setRestaurantScreenTopTotals] = useState<{ totalAmount: number; totalQuantity: number }>({ totalAmount: 0, totalQuantity: 0 });
  const [loadingRestaurantScreenTop, setLoadingRestaurantScreenTop] = useState(false);

  // ✅ YENİ: Paket sipariş ekranı bazlı en çok satanlar (paket ekranı seçimli 3'lü kart takımı)
  const [selectedPackageScreens, setSelectedPackageScreens] = useState<SelectedScreen[]>(() => loadScreenSelection('package'));
  const [packageScreenSelectOpen, setPackageScreenSelectOpen] = useState(false);
  const [packageScreenTopProducts, setPackageScreenTopProducts] = useState<ScreenTopSellingProduct[]>([]);
  const [packageScreenTopTotals, setPackageScreenTopTotals] = useState<{ totalAmount: number; totalQuantity: number }>({ totalAmount: 0, totalQuantity: 0 });
  const [loadingPackageScreenTop, setLoadingPackageScreenTop] = useState(false);

  // ✅ YENİ: Kullanıcı tanımlı dinamik rapor kartları (localStorage'da ReportSpec olarak)
  const [customReports, setCustomReports] = useState<ReportSpec[]>(loadCustomReports);
  const [reportBuilderOpen, setReportBuilderOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ReportSpec | null>(null);
  const [aiReportOpen, setAiReportOpen] = useState(false);
  // Backend'de AI anahtarı yapılandırılmamışsa buton hiç gösterilmez (feature-degrade)
  const [aiReportEnabled, setAiReportEnabled] = useState(false);

  // Açılışta backend rapor kataloğunu çek: sürüm kayması varsa console.warn
  useEffect(() => {
    let active = true;
    salesService
      .getDynamicReportCatalog()
      .then((catalog) => {
        if (!active) return;
        verifyReportCatalogAgainstBackend(catalog.sources);
      })
      .catch((err) => {
        console.warn('[Dashboard] rapor kataloğu alınamadı (sürüm karşılaştırması atlandı):', err?.message);
      });
    // AI rapor önerisi aktiflik bilgisi gts_ai_api'den gelir (eskiden katalog yanıtındaydı)
    aiChatService
      .getAiReportStatus()
      .then((enabled) => {
        if (active) setAiReportEnabled(enabled);
      })
      .catch((err) => {
        console.warn('[Dashboard] AI rapor durumu alınamadı (buton gizlendi):', err?.message);
      });
    return () => {
      active = false;
    };
  }, []);

  // AI chat penceresinden "Dashboard'a ekle" yapıldığında listeyi canlı yenile
  useEffect(() => {
    const handleCustomReportsChanged = () => setCustomReports(loadCustomReports());
    window.addEventListener('gts:customReportsChanged', handleCustomReportsChanged);
    return () => window.removeEventListener('gts:customReportsChanged', handleCustomReportsChanged);
  }, []);

  const customReportExtraCards = useMemo<ExtraCardDef[]>(
    () =>
      customReports.map((report) => ({
        id: report.id,
        defaultLayout:
          report.visual === 'kpi'
            ? { x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 }
            : { x: 0, y: 0, w: 3, h: 7, minW: 2, minH: 3 },
      })),
    [customReports]
  );

  // ✅ YENİ: Özelleştirilebilir dashboard düzeni (grid + görünürlük)
  const { layout, hiddenCards, saveLayout, toggleCardVisibility } = useDashboardLayout(customReportExtraCards);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const [isLayoutEditMode, setIsLayoutEditMode] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<Layout[]>([]);
  const [viewSettingsOpen, setViewSettingsOpen] = useState(false);
  const gridWrapperRef = useRef<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState<number>(1200);

  // ✅ Grid genişliğini konteynerden ölç (react-grid-layout sabit piksel genişliği ister)
  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el) return;
    const update = () => {
      if (el.offsetWidth > 0) setGridWidth(el.offsetWidth);
    };
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    window.addEventListener('resize', update);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isMobileLayout]);

  // ✅ Düzenleme modu işlemleri
  const handleEnterLayoutEdit = () => {
    setPendingLayout(layout.map((l) => ({ ...l })));
    setIsLayoutEditMode(true);
    setSettingsMenuAnchor(null);
  };

  const handleSaveLayoutEdit = () => {
    saveLayout(pendingLayout);
    setIsLayoutEditMode(false);
    setPendingLayout([]);
  };

  const handleCancelLayoutEdit = () => {
    setIsLayoutEditMode(false);
    setPendingLayout([]);
  };

  const handleResetPendingLayout = () => {
    // Sadece taslağı varsayılana döndürür; "Kaydet" ile kalıcı olur (özel rapor kartları dahil)
    setPendingLayout(buildDefaultLayoutWithExtras(customReportExtraCards));
  };

  // ✅ YENİ: Dinamik rapor kartı işlemleri
  const handleSaveCustomReport = (spec: ReportSpec) => {
    setCustomReports(upsertCustomReport(spec));
    setEditingReport(null);
  };

  const handleEditCustomReport = (spec: ReportSpec) => {
    setEditingReport(spec);
    setReportBuilderOpen(true);
  };

  const handleDeleteCustomReport = (spec: ReportSpec) => {
    setCustomReports(deleteCustomReport(spec.id));
  };

  const handleOpenReportBuilder = () => {
    setEditingReport(null);
    setReportBuilderOpen(true);
  };

  const handleOpenAiReport = () => {
    setAiReportOpen(true);
  };

  // AI önerisini sihirbazda aç: kullanıcı adım adım düzeltebilir, kaydedince aynı depoya yazılır
  const handleEditAiSuggestionInBuilder = (spec: ReportSpec) => {
    setEditingReport(spec);
    setReportBuilderOpen(true);
  };

  // Grid'e verilecek layout: düzenleme modunda taslak, normalde kayıtlı düzen (gizli kartlar hariç)
  const activeLayout = (isLayoutEditMode ? pendingLayout : layout).filter(
    (l) => !hiddenCards.includes(l.i)
  );

  // Mobil dizilim sırası: kayıtlı düzendeki (y, x) konumuna göre
  const visibleCardIdsOrdered = [...layout]
    .filter((l) => !hiddenCards.includes(l.i))
    .sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y))
    .map((l) => l.i);

  // 📱 Mobilde kartlar tek sütunda alt alta dizildiği için okuma önceliği masaüstü
  //    grid'inden farklı: en üstte günün satışları, en altta istatistik kartları.
  //    Kullanıcının kaydettiği masaüstü düzeni bundan etkilenmez.
  const mobileCardIdsOrdered = (() => {
    const statsIds = new Set(
      DASHBOARD_CARDS.filter((c) => c.group === 'stats').map((c) => c.id)
    );
    const branchSales = visibleCardIdsOrdered.filter((id) => id === 'widgetBranchSales');
    const rest = visibleCardIdsOrdered.filter(
      (id) => id !== 'widgetBranchSales' && !statsIds.has(id)
    );
    const stats = visibleCardIdsOrdered.filter((id) => statsIds.has(id));
    return [...branchSales, ...rest, ...stats];
  })();
  const [dateFilter, setDateFilter] = useState<'daily' | 'yesterday' | 'weekly' | 'monthly' | 'yearly' | 'custom'>('daily'); // ✅ Varsayılan: günlük
  // ✅ Özel tarih aralığı filtresi
  const [isCustomDateActive, setIsCustomDateActive] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [usdRate, setUsdRate] = useState<number | null>(null);
  const [eurRate, setEurRate] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingInventory, setLoadingInventory] = useState<boolean>(true);
  const [loadingBankBalance, setLoadingBankBalance] = useState<boolean>(true);
  const [loadingCashboxBalance, setLoadingCashboxBalance] = useState<boolean>(true);
  const [loadingWarehouseCount, setLoadingWarehouseCount] = useState<boolean>(false);
  const [loadingPersonnel, setLoadingPersonnel] = useState<boolean>(true); // ✅ YENİ
  const [loadingTasks, setLoadingTasks] = useState<boolean>(true); // ✅ YENİ
  const [loadingCurrencyRates, setLoadingCurrencyRates] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Şube satış detay modalı
  const [branchDetailOpen, setBranchDetailOpen] = useState(false);
  const [branchDetailBranchId, setBranchDetailBranchId] = useState<string | null>(null);
  const [branchDetailBranchName, setBranchDetailBranchName] = useState('');
  const [branchDetailData, setBranchDetailData] = useState<any[]>([]);
  const [branchDetailLoading, setBranchDetailLoading] = useState(false);
  const [branchDetailPage, setBranchDetailPage] = useState(1);
  const [branchDetailPageSize, setBranchDetailPageSize] = useState(25);
  const [branchDetailTotalCount, setBranchDetailTotalCount] = useState(0);
  const [branchDetailTotalPages, setBranchDetailTotalPages] = useState(0);
  const [branchDetailTotalAmount, setBranchDetailTotalAmount] = useState(0);
  const [branchDetailTotalQuantity, setBranchDetailTotalQuantity] = useState(0);

  // ✅ Tüm şubeler modalı — satışı olmayan şubeler de 0 ile listelenir.
  const [allBranchesOpen, setAllBranchesOpen] = useState(false);
  const [allBranchesLoading, setAllBranchesLoading] = useState(false);
  const [allBranchesSummaries, setAllBranchesSummaries] = useState<BranchSalesSummary[]>([]);

  // ✅ Aşağı çekerek yenileme (pull-to-refresh)
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const refreshingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const loadRef = useRef<() => Promise<void>>(async () => {});
  const PULL_THRESHOLD = 70;
  const PULL_MAX = 110;

  useEffect(() => { refreshingRef.current = refreshing; }, [refreshing]);
  useEffect(() => { pullDistanceRef.current = pullDistance; }, [pullDistance]);

  // ✅ Manager ise şubeyi prop'tan kilitle
  useEffect(() => {
    if (isManager && propBranchId && selectedBranchId !== propBranchId) {
      setSelectedBranchId(propBranchId);
    }
  }, [isManager, propBranchId]);

  // Şirket ID'sini ve dashboard verilerini yükleme
  const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Kullanıcı bilgilerini kontrol et
        if (!userDetails?.companyId) {
          console.error('❌ [DASHBOARD] companyId yok');
          setLoading(false);
          setError('Şirket bilgisi bulunamadı.');
          return;
        }
        
        const companyId = userDetails.companyId;
        console.log('✅ [DASHBOARD] companyId:', companyId);

        // ⏱️ Her bir yüklemenin süresini ölç → hangi adımın yavaş olduğunu görmek için.
        const t0 = performance.now();
        const timed = <T,>(label: string, p: Promise<T>): Promise<T> => {
          const s = performance.now();
          return p.finally(() => {
            console.log(`⏱️ [DASHBOARD-TIMING] ${label}: ${Math.round(performance.now() - s)}ms`);
          });
        };
        
        // ✅ Tüm bağımsız API çağrılarını paralel çalıştır (sıralı değil!)
        // Her tarih filtresinde satış bazlı özet (SQL GROUP BY, server-side).
        const summaryCall = loadBranchSalesSummary(companyId, dateFilter, selectedBranchId);

        // ⚡ KRİTİK (hızlı) grup — yenileme göstergesi YALNIZCA bunları bekler (~1sn).
        const criticalGroup = Promise.allSettled([
          timed('satışÖzeti', summaryCall),
          timed('bankaBakiye', loadBankBalance(companyId, selectedBranchId)),
          timed('kasaBakiye', loadCashboxBalance(companyId, selectedBranchId)),
          timed('stokÜrünSayısı', loadInventoryItemCount(companyId)),
          timed('bankaHesapları', loadBankAccounts(companyId, selectedBranchId)),
          timed('personelSayısı(CloudFn)', loadPersonnelCount(companyId)),
          timed('görevSayısı(CloudFn)', loadActiveTasksCount(companyId)),
        ]);

        // 🐢 AĞIR grup — arka planda devam eder; kendi widget spinner'ları olduğundan
        //   yenileme göstergesini bekletmez (kasa listesi ~6sn, en çok satanlar ~3sn).
        timed('kasalar', loadCashboxes(companyId, selectedBranchId)).catch(() => {});
        timed('enÇokSatanlar', loadBestSellingProducts(companyId, dateFilter, selectedBranchId)).catch(() => {});

        await criticalGroup;
        console.log(`⏱️ [DASHBOARD-TIMING] ▶ KRİTİK grup tamam (gösterge kapanır): ${Math.round(performance.now() - t0)}ms`);
        
        // Döviz kurlarını yükle
        loadCurrencyRates();
        
        setLoading(false);
        
      } catch (error) {
        console.error('❌ [DASHBOARD] Veri yüklenirken hata:', error);
        setLoading(false);
        setError('Veriler yüklenirken bir hata oluştu.');
      }
  };

  // En güncel yükleme fonksiyonunu ref'te tut (pull-to-refresh handler'ları için).
  loadRef.current = loadDashboardData;

  useEffect(() => {
    if (userDetails?.companyId) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?.companyId, selectedBranchId, branches]);

  // ✅ Tüm dashboard verilerini yeniden yükle (pull-to-refresh / yenile butonu)
  const triggerRefresh = useCallback(async () => {
    if (refreshingRef.current) return;
    setRefreshing(true);
    refreshingRef.current = true;
    try {
      await loadRef.current();
    } finally {
      setRefreshing(false);
      refreshingRef.current = false;
    }
  }, []);

  // ✅ Aşağı çekerek yenileme: dokunmatik (pull-down) + masaüstü (tepe noktasında yukarı kaydırma)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const getScrollParent = (node: HTMLElement | null): HTMLElement => {
      let p = node?.parentElement || null;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
        p = p.parentElement;
      }
      return (document.scrollingElement as HTMLElement) || document.documentElement;
    };
    const scroller = getScrollParent(el);

    let startY = 0;
    let pulling = false;

    // Sekmeler display:none ile mount'ta tutulduğu için, Dashboard gizliyken
    // (başka sekme aktifken) paylaşılan scroll container olaylarını yok say.
    const isVisible = () => el.offsetParent !== null;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current || !isVisible()) return;
      if (scroller.scrollTop <= 0) {
        startY = e.touches[0].clientY;
        pulling = true;
      } else {
        pulling = false;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!pulling || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > 0 && scroller.scrollTop <= 0) {
        const dist = Math.min(dy * 0.5, PULL_MAX);
        setIsPulling(true);
        setPullDistance(dist);
        if (dist > 4 && e.cancelable) e.preventDefault();
      } else if (pullDistanceRef.current !== 0) {
        setPullDistance(0);
      }
    };
    const finishPull = () => {
      if (!pulling) return;
      pulling = false;
      setIsPulling(false);
      if (pullDistanceRef.current >= PULL_THRESHOLD) triggerRefresh();
      setPullDistance(0);
    };

    // Not: Masaüstünde (mouse wheel) yenileme kaldırıldı; yukarı kaydırırken
    // yanlışlıkla tetikleniyordu. Masaüstünde yenileme için baştaki "Yenile"
    // butonu kullanılır. Aşağı-çekerek yenileme yalnızca dokunmatik cihazlarda aktif.
    scroller.addEventListener('touchstart', onTouchStart, { passive: true });
    scroller.addEventListener('touchmove', onTouchMove, { passive: false });
    scroller.addEventListener('touchend', finishPull);
    scroller.addEventListener('touchcancel', finishPull);

    return () => {
      scroller.removeEventListener('touchstart', onTouchStart);
      scroller.removeEventListener('touchmove', onTouchMove);
      scroller.removeEventListener('touchend', finishPull);
      scroller.removeEventListener('touchcancel', finishPull);
    };
  }, [triggerRefresh]);
  
  // ✅ Tarih filtresi değiştiğinde tarih-bağımlı verileri yeniden yükle
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);
  useEffect(() => {
    if (!dateFilterInitialized) {
      setDateFilterInitialized(true);
      return; // İlk render'da çalışmasın, loadDashboardData zaten çağırıyor
    }
    // Custom modda tarihler henüz seçilmemişse API çağrısı yapma
    if (dateFilter === 'custom' && (!customStartDate || !customEndDate)) {
      return;
    }
    if (userDetails?.companyId) {
      loadBestSellingProducts(userDetails.companyId, dateFilter, selectedBranchId);
      // Her filtrede satış bazlı özet (server-side agregasyon)
      loadBranchSalesSummary(userDetails.companyId, dateFilter, selectedBranchId);
    }
  }, [dateFilter]);

  // Depo sayısını hesapla (AuthContext'ten warehouses kullanarak)
  useEffect(() => {
    if (warehouses && warehouses.length > 0) {
      // Sadece aktif depoları say (eğer isActive özelliği varsa)
      const activeWarehouses = warehouses.filter((warehouse: any) => 
        warehouse.isActive !== false
      );
      setWarehouseCount(activeWarehouses.length);
      console.log('✅ [DASHBOARD] Toplam aktif depo sayısı:', activeWarehouses.length);
    } else {
      setWarehouseCount(0);
    }
  }, [warehouses]);

  // Cari hesap sayısını yükleme
  // Stok ürün sayısını yükleme
  const loadInventoryItemCount = async (companyId: string) => {
    try {
      setLoadingInventory(true);
      
      // Sayfalı endpoint'i kullan (minimal veri çekimi için pageSize=1)
      const apiUrl = `/api/Inventory/items/paged`;
      console.log('🌐 [DASHBOARD] Stok ürünleri API çağrısı:', apiUrl);
      
      const response = await accountingApi.post(apiUrl, {
        pageNumber: 1,
        pageSize: 1,
        isActive: true
      });

      const result = response.data;
      const totalCount = result.totalCount || 0;
      
      console.log('✅ [DASHBOARD] Toplam aktif stok ürünü:', totalCount);
      setInventoryItemCount(totalCount);
      setLoadingInventory(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] Stok ürün sayısı yüklenemedi:', error);
      setLoadingInventory(false);
      setError(handleApiError(error));
    }
  };

  // Toplam banka bakiyesini yükleme (şube filtreli)
  const loadBankBalance = async (companyId: string, branchId?: string | null) => {
    try {
      setLoadingBankBalance(true);
      
      // ✅ Şube filtresi varsa dashboard endpoint'ini kullan (zaten branchId destekli)
      const apiUrl = branchId 
        ? ACCOUNTING_API.BANK_ACCOUNT_DASHBOARD(branchId)
        : ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY;
      console.log('🌐 [DASHBOARD] Banka hesapları API çağrısı:', apiUrl);
      
      const response = await accountingApi.get(apiUrl);

      const result = response.data;
      const accounts = branchId ? result : (result.data || result);
      
      // Aktif hesapların bakiyelerini topla
      let totalBalance = 0;
      accounts.forEach((account: any) => {
        if (account.isActive !== false) {
          totalBalance += parseFloat(account.balance) || 0;
          
          // Alt hesapları da hesaba kat
          if (account.subAccounts && Array.isArray(account.subAccounts)) {
            account.subAccounts.forEach((subAccount: any) => {
              if (subAccount.isActive !== false) {
                totalBalance += parseFloat(subAccount.balance) || 0;
              }
            });
          }
        }
      });
      
      console.log('✅ [DASHBOARD] Toplam banka bakiyesi:', totalBalance, branchId ? `(Şube: ${branchId})` : '(Tüm şubeler)');
      setTotalBankBalance(totalBalance);
      setLoadingBankBalance(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] Banka bakiyesi yüklenemedi:', error);
      setLoadingBankBalance(false);
      setError(handleApiError(error));
    }
  };

  // Toplam kasa bakiyesini yükleme (şube filtreli)
  const loadCashboxBalance = async (companyId: string, branchId?: string | null) => {
    try {
      setLoadingCashboxBalance(true);
      
      // ✅ Şube filtresi varsa dashboard endpoint'ini kullan (zaten branchId destekli)
      const apiUrl = branchId
        ? ACCOUNTING_API.CASHBOX_DASHBOARD(branchId)
        : ACCOUNTING_API.CASHBOX_BY_COMPANY;
      console.log('🌐 [DASHBOARD] Kasalar API çağrısı:', apiUrl);
      
      const response = await accountingApi.get(apiUrl);

      const result = response.data;
      const cashboxList = branchId ? result : (result.data || result);
      
      // Aktif kasaların bakiyelerini topla
      let totalBalance = 0;
      cashboxList.forEach((cashbox: any) => {
        if (cashbox.isActive !== false) {
          totalBalance += parseFloat(cashbox.balance) || 0;
        }
      });
      
      console.log('✅ [DASHBOARD] Toplam kasa bakiyesi:', totalBalance, branchId ? `(Şube: ${branchId})` : '(Tüm şubeler)');
      setTotalCashboxBalance(totalBalance);
      setLoadingCashboxBalance(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] Kasa bakiyesi yüklenemedi:', error);
      setLoadingCashboxBalance(false);
      setError(handleApiError(error));
    }
  };

  // Döviz kurlarını yükleme
  const loadCurrencyRates = async () => {
    try {
      setLoadingCurrencyRates(true);
      
      // CurrencyService'i kullanarak döviz kurlarını getir
      const currencyRates = await CurrencyService.fetchExchangeRates();
      
      // USD ve EUR kurlarını bul ve kaydet
      const usdCurrency = currencyRates.find(currency => currency.code === 'USD');
      const eurCurrency = currencyRates.find(currency => currency.code === 'EUR');
      
      if (usdCurrency) {
        setUsdRate(usdCurrency.selling); // Efektif satış kurunu kullan
      }
      
      if (eurCurrency) {
        setEurRate(eurCurrency.selling); // Efektif satış kurunu kullan
      }
      
      setLoadingCurrencyRates(false);
    } catch (error) {
      console.error('Döviz kurları yüklenirken hata:', error);
      setLoadingCurrencyRates(false);
    }
  };

  // ✅ YENİ: Toplam personel sayısını yükleme
  const loadPersonnelCount = async (companyId: string) => {
    try {
      setLoadingPersonnel(true);
      
      console.log('🌐 [DASHBOARD] Personel verileri yükleniyor...');
      
      // Firebase'den personel listesini getir
      const personnelData = await firebaseDataService.getPersonnel(companyId);
      
      let personnelList = [];
      if (Array.isArray(personnelData)) {
        personnelList = personnelData;
      } else if (personnelData && typeof personnelData === 'object') {
        personnelList = Object.values(personnelData);
      }
      
      // Silinen personelleri filtrele - sadece aktif personelleri say
      const activePersonnel = personnelList.filter((person: any) => !person.isDeleted);
      const count = activePersonnel.length;
      
      console.log('✅ [DASHBOARD] Toplam aktif personel:', count);
      setTotalPersonnel(count);
      setLoadingPersonnel(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] Personel sayısı yüklenemedi:', error);
      setLoadingPersonnel(false);
      setError(handleApiError(error));
    }
  };

  // ✅ YENİ: Aktif görev sayısını yükleme
  const loadActiveTasksCount = async (companyId: string) => {
    try {
      setLoadingTasks(true);
      
      console.log('🌐 [DASHBOARD] Görev verileri yükleniyor (SQL)...');
      
      // Faz 5: Aktif görev sayısı SQL'den (taskDataService) — accepted + assigned.
      // Yalnızca totalCount gerektiği için pageSize=1 ile iki durum sorgulanır.
      const [acceptedRes, assignedRes] = await Promise.all([
        taskDataService.listTasks({ status: 'accepted', page: 1, pageSize: 1 }),
        taskDataService.listTasks({ status: 'assigned', page: 1, pageSize: 1 }),
      ]);
      const activeTasksCount = (acceptedRes.totalCount || 0) + (assignedRes.totalCount || 0);
      
      console.log('✅ [DASHBOARD] Toplam aktif görev:', activeTasksCount);
      setActiveTasks(activeTasksCount);
      setLoadingTasks(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] Görev sayısı yüklenemedi:', error);
      setLoadingTasks(false);
      setError(handleApiError(error));
    }
  };

  // ✅ GÜNCELLEME: Kasaları yükleme (SQL'de optimize edilmiş)
  const loadCashboxes = async (companyId: string, branchId?: string | null) => {
    try {
      setLoadingCashboxes(true);
      
      console.log('🌐 [DASHBOARD] Kasalar yükleniyor (SQL optimize)...', branchId ? `Şube: ${branchId}` : 'Tüm şubeler');
      
      // ✅ SQL'de sadece gerekli alanları al ve sıralama yap
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_DASHBOARD(branchId));
      
      const dashboardCashboxes = response.data;
      
      // SQL'den gelen veriyi frontend formatına çevir
      const cashboxList: CashboxItem[] = dashboardCashboxes.map((data: any) => ({
        id: data.id.toString(),
        name: data.name || '',
        description: data.description || '',
        currency: data.currency || 'TRY',
        balance: parseFloat(data.balance) || 0,
        createdAt: data.createdAt || '',
        isActive: data.isActive !== false,
        branchId: data.branchId || null,
        companyBranchName: data.companyBranchName || null,
        isDefault: data.isDefault || false,
        hasTransactions: false
      }));
      
      console.log('✅ [DASHBOARD] Kasalar yüklendi (SQL):', cashboxList.length, 'adet');
      setCashboxes(cashboxList);
      setLoadingCashboxes(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] Kasalar yüklenemedi:', error);
      setLoadingCashboxes(false);
      setError(handleApiError(error));
    }
  };

  // ✅ GÜNCELLEME: Banka hesaplarını yükleme (SQL'de optimize edilmiş, alt hesaplar dahil)
  const loadBankAccounts = async (companyId: string, branchId?: string | null) => {
    try {
      setLoadingBankAccounts(true);
      
      console.log('🌐 [DASHBOARD] Banka hesapları yükleniyor (SQL optimize)...', branchId ? `Şube: ${branchId}` : 'Tüm şubeler');
      
      // ✅ SQL'de sadece gerekli alanları al, sıralama yap, alt hesaplar dahil
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_DASHBOARD(branchId));
      
      const dashboardAccounts = response.data;
      
      // SQL'den gelen veriyi frontend formatına çevir
      const accountsList: BankAccount[] = dashboardAccounts.map((data: any) => {
        // Alt hesapları dönüştür
        const subAccountsObj: { [key: string]: SubAccount } = {};
        if (data.subAccounts && Array.isArray(data.subAccounts)) {
          data.subAccounts.forEach((subAccount: any) => {
            subAccountsObj[subAccount.id.toString()] = {
              id: subAccount.id.toString(),
              accountName: subAccount.accountName || '',
              accountNumber: subAccount.accountNumber || '',
              iban: subAccount.iban || '',
              currency: subAccount.currency || 'TRY',
              balance: parseFloat(subAccount.balance) || 0,
              isActive: subAccount.isActive !== false,
              parentId: data.id.toString()
            };
          });
        }
        
        return {
          id: data.id.toString(),
          bankName: data.bankName || '',
          branchName: data.branchName || '',
          accountNumber: data.accountNumber || '',
          iban: data.iban || '',
          currency: data.currency || 'TRY',
          balance: parseFloat(data.balance) || 0,
          isActive: data.isActive !== false,
          branchId: data.branchId || null,
          companyBranchName: data.companyBranchName || null,
          subAccounts: Object.keys(subAccountsObj).length > 0 ? subAccountsObj : undefined
        };
      });
      
      console.log('✅ [DASHBOARD] Banka hesapları yüklendi (SQL):', accountsList.length, 'adet');
      setBankAccounts(accountsList);
      setLoadingBankAccounts(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] Banka hesapları yüklenemedi:', error);
      setLoadingBankAccounts(false);
      setError(handleApiError(error));
    }
  };

  // ✅ Şube bazlı satış özeti yükleme — her tarih filtresi için tek yol.
  //   Backend SQL GROUP BY + SUM + COUNT yapar (fiş + restoran), client'a şube
  //   başına tek satır gelir; client-side hesap yok.
  const branchSalesAbortRef = React.useRef<AbortController | null>(null);
  const loadBranchSalesSummary = async (companyId: string, filter: 'daily' | 'yesterday' | 'weekly' | 'monthly' | 'yearly' | 'custom', branchId?: string | null) => {
    try {
      // Önceki isteği iptal et (çift çağrı önleme)
      if (branchSalesAbortRef.current) {
        branchSalesAbortRef.current.abort();
      }
      const abortController = new AbortController();
      branchSalesAbortRef.current = abortController;

      setLoadingBranchSales(true);

      const { startDate, endDate } = getDateRange(filter);

      console.log('🌐 [DASHBOARD] Şube satış özeti yükleniyor (server-side agregasyon)...', branchId ? `Şube: ${branchId}` : 'Tüm şubeler', `Filtre: ${filter}`);

      // ✅ Gün Sonu raporuyla AYNI kaynaklar (QuickSaleReceipts + RestaurantCompletedSales):
      // ÖKC'siz nakit + paket + tüm kanallar dahil, manuel/hizmet kalemleri dahil.
      const summaryData = await salesService.getBranchSalesSummary(startDate, endDate, branchId);

      const allSummaries: BranchSalesSummary[] = summaryData.map((item: any) => ({
        branchId: item.BranchId ?? item.branchId ?? null,
        totalBalance: parseFloat(item.TotalAmount ?? item.totalAmount) || 0,
        transactionCount: item.ReceiptCount ?? item.receiptCount ?? 0,
      }));

      // ✅ GÜVENLİK: Sadece bu şirkete ait şubeleri göster, başka şirketlerin şubelerini filtrele
      // branches henüz yüklenmediyse filtreleme yapma (API zaten X-Company-Id ile şirkete göre filtreler)
      const companyBranchIds = (branches || []).map((b: any) => b.id || b.branchId);
      const summaries = companyBranchIds.length > 0
        ? allSummaries.filter(s => s.branchId === null || companyBranchIds.includes(s.branchId))
        : allSummaries;

      // Bu istek sırasında yeni bir istek geldiyse (filtre değişti) sonucu yazma.
      if (abortController.signal.aborted) {
        console.log('🔄 [DASHBOARD] Şube satış özeti isteği eskidi, sonuç yazılmadı');
        return;
      }

      console.log('✅ [DASHBOARD] Şube satış özeti yüklendi:', summaries.length, 'şube', summaries);
      setBranchSummaries(summaries);
      setLoadingBranchSales(false);

    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('🔄 [DASHBOARD] Şube satış özeti isteği iptal edildi (yeni istek geldi)');
        return;
      }
      console.error('❌ [DASHBOARD] Şube satış özeti yüklenemedi:', error);
      setBranchSummaries([]);
      setLoadingBranchSales(false);
    }
  };

  // ✅ Tüm şubeler modalı: şube filtresinden BAĞIMSIZ olarak tüm şubelerin özeti çekilir.
  //    Aksi halde ekranda bir şube filtresi seçiliyken diğer şubeler yanlışlıkla 0 görünürdü.
  const loadAllBranchesSummary = async () => {
    try {
      setAllBranchesLoading(true);
      const { startDate, endDate } = getDateRange(dateFilter);
      const summaryData = await salesService.getBranchSalesSummary(startDate, endDate, null);

      const summaries: BranchSalesSummary[] = (summaryData || []).map((item: any) => ({
        branchId: item.BranchId ?? item.branchId ?? null,
        totalBalance: parseFloat(item.TotalAmount ?? item.totalAmount) || 0,
        transactionCount: item.ReceiptCount ?? item.receiptCount ?? 0,
      }));

      setAllBranchesSummaries(summaries);
    } catch (error) {
      console.error('❌ [DASHBOARD] Tüm şube satış özeti yüklenemedi:', error);
      setAllBranchesSummaries([]);
    } finally {
      setAllBranchesLoading(false);
    }
  };

  // Modal açıkken tarih filtresi değişirse liste tazelenir.
  useEffect(() => {
    if (!allBranchesOpen) return;
    if (dateFilter === 'custom' && (!customStartDate || !customEndDate)) return;
    loadAllBranchesSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBranchesOpen, dateFilter, customStartDate, customEndDate]);

  // ✅ Şirketteki HER şube listelenir; dönem içinde satışı olmayan şube 0 ile görünür.
  //    Şubesiz (BranchId = null) satışlar varsa ayrı bir satır olarak eklenir.
  const allBranchRows = useMemo(() => {
    const rows = (branches || []).map((b: any) => {
      const id = b.id || b.branchId;
      const match = allBranchesSummaries.find(s => s.branchId === id);
      return {
        branchId: (id ?? null) as string | null,
        branchName: b.name || b.branchName || id,
        totalBalance: match?.totalBalance ?? 0,
        transactionCount: match?.transactionCount ?? 0,
      };
    });

    const nullSummary = allBranchesSummaries.find(s => s.branchId === null);
    if (nullSummary) {
      rows.push({
        branchId: null,
        branchName: t('dashboard.allBranchesOverall'),
        totalBalance: nullSummary.totalBalance,
        transactionCount: nullSummary.transactionCount,
      });
    }

    return rows.sort((a, b) => b.totalBalance - a.totalBalance);
  }, [branches, allBranchesSummaries, t]);

  // Şube satış detayını yükle (server-side pagination)
  const loadBranchSalesDetail = async (branchId: string | null, page: number, pageSize: number) => {
    if (!userDetails?.companyId) return;
    setBranchDetailLoading(true);
    try {
      // Aktif tarih filtresinin aralığı kullanılır — özet ile aynı dönem.
      const { startDate, endDate } = getDateRange(dateFilter);

      // ✅ Fiş kalemleri (satılan ürünler) — özet ile AYNI kaynaktan (QuickSaleReceipts→Invoice→Items).
      // Manuel/hizmet (stoksuz) kalemler de dahildir; böylece detay toplamı şube özetiyle tutarlı.
      // groupByProduct: aynı ürün tek satırda toplanır; gruplama ve sayfalama SQL tarafındadır,
      // aksi halde aynı ürün farklı sayfalara düşüp gruplama görünürde bozulurdu.
      const result = await salesService.getBranchSalesItems({
        startDate,
        endDate,
        branchId,
        page,
        pageSize,
        groupByProduct: true,
      });

      setBranchDetailData(result.data || []);
      setBranchDetailTotalCount(result.totalCount || 0);
      setBranchDetailTotalPages(result.totalPages || 0);
      setBranchDetailTotalAmount(result.totalAmount || 0);
      setBranchDetailTotalQuantity(result.totalQuantity || 0);
    } catch (err) {
      console.error('❌ [DASHBOARD] Şube satış detayı yüklenemedi:', err);
      setBranchDetailData([]);
      setBranchDetailTotalCount(0);
      setBranchDetailTotalPages(0);
      setBranchDetailTotalAmount(0);
      setBranchDetailTotalQuantity(0);
    } finally {
      setBranchDetailLoading(false);
    }
  };

  const handleBranchDetailOpen = (summary: BranchSalesSummary, branchName: string) => {
    setBranchDetailBranchId(summary.branchId);
    setBranchDetailBranchName(branchName);
    setBranchDetailPage(1);
    setBranchDetailOpen(true);
    loadBranchSalesDetail(summary.branchId, 1, branchDetailPageSize);
  };

  const handleBranchDetailPageChange = (newPage: number) => {
    setBranchDetailPage(newPage);
    loadBranchSalesDetail(branchDetailBranchId, newPage, branchDetailPageSize);
  };

  /**
   * Satırın listedeki şube sırasını döner (1'den başlar). Şubesi olmayan toplam
   * satırı ("Tüm Şubeler (Genel)") bir şube olmadığı için numaralandırılmaz;
   * bu yüzden sıra, dizideki index değil şubeli satırların sayımıdır.
   */
  const branchDisplayNo = (rows: Array<{ branchId: string | null }>, index: number): number | null => {
    if (!rows[index]?.branchId) return null;
    return rows.slice(0, index + 1).filter(r => r.branchId).length;
  };

  const handleBranchDetailPageSizeChange = (newSize: number) => {
    setBranchDetailPageSize(newSize);
    setBranchDetailPage(1);
    loadBranchSalesDetail(branchDetailBranchId, 1, newSize);
  };

  // ✅ YENİ: Tarih aralığını hesapla (günlük/dün/haftalık/aylık/yıllık/özel)
  // Gün başlangıcı (00:00:00) ve gün sonu (23:59:59) kullanarak tam gün kapsama sağlar
  const getDateRange = (filter: 'daily' | 'yesterday' | 'weekly' | 'monthly' | 'yearly' | 'custom'): { startDate: string; endDate: string } => {
    // ✅ Özel tarih aralığı aktifse, kullanıcının seçtiği tarihleri kullan
    if (filter === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: customStartDate.toISOString(),
        endDate: customEndDate.toISOString()
      };
    }

    const now = new Date();

    // ✅ Dün: bugünü kapsamayan tek günlük aralık, bu yüzden endDate de bir gün geriye alınır
    if (filter === 'yesterday') {
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0).toISOString(),
        endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999).toISOString()
      };
    }

    // endDate: bugünün sonu (23:59:59.999)
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    let startDate: Date;

    switch (filter) {
      case 'daily':
        // Bugünün başlangıcı (00:00:00)
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        break;
      case 'weekly':
        // 7 gün öncenin başlangıcı (00:00:00)
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
        break;
      case 'monthly':
        // 30 gün öncenin başlangıcı (00:00:00)
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0, 0);
        break;
      case 'yearly':
        // 365 gün öncenin başlangıcı (00:00:00)
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 365, 0, 0, 0, 0);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  };

  // ✅ YENİ: Dinamik rapor kartlarının kullandığı aktif tarih aralığı.
  // Memoize edilir ki kartlar her render'da yeniden fetch etmesin.
  const dynamicReportRange = useMemo(
    () => getDateRange(dateFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateFilter, isCustomDateActive, customStartDate, customEndDate]
  );

  // ✅ Özel tarih aralığı süre hesaplama
  const getCustomDateRangeLabel = (): string => {
    if (!customStartDate || !customEndDate) return '';
    const diffMs = customEndDate.getTime() - customStartDate.getTime();
    if (diffMs <= 0) return t('dashboard.customRangeInvalid');
    const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) {
      return hours > 0
        ? t('dashboard.customRangeDayHour', { days, hours })
        : t('dashboard.customRangeDay', { days });
    }
    return hours > 0
      ? (minutes > 0
        ? t('dashboard.customRangeHourMinute', { hours, minutes })
        : t('dashboard.customRangeHour', { hours }))
      : t('dashboard.customRangeMinute', { minutes });
  };

  // ✅ Tarihi Türkçe formatla (modal içi gösterim)
  const formatDateForDisplay = (date: Date | null): string => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  // ✅ YENİ: En çok satan ürünleri yükleme (SQL'de gruplama ile - performanslı, tarih filtreli)
  const loadBestSellingProducts = async (companyId: string, filter: 'daily' | 'yesterday' | 'weekly' | 'monthly' | 'yearly' | 'custom' = 'daily', branchId?: string | null) => {
    try {
      setLoadingBestSelling(true);
      
      console.log(`🌐 [DASHBOARD] En çok satan ürünler yükleniyor (${filter})...`, branchId ? `Şube: ${branchId}` : 'Tüm şubeler');
      
      // Tarih aralığını hesapla
      const { startDate, endDate } = getDateRange(filter);
      
      // ✅ SQL'de gruplama ve sıralama yapan endpoint'i kullan (tarih ve şube filtreleri ile)
      const response = await accountingApi.get(
        ACCOUNTING_API.STOCK_MOVEMENTS_TOP_SELLING(11, startDate, endDate, branchId)
      );
      
      const productSalesArray: ProductSales[] = response.data;
      
      console.log(`✅ [DASHBOARD] En çok satan ürünler (${filter}):`, productSalesArray);
      setBestSellingProducts(productSalesArray);
      setLoadingBestSelling(false);
      
    } catch (error: any) {
      console.error('❌ [DASHBOARD] En çok satan ürünler yüklenemedi:', error);
      setLoadingBestSelling(false);
      setError(handleApiError(error));
    }
  };

  // ✅ YENİ: Ekran bazlı en çok satanlar — seçili hızlı satış ekranları + aktif tarih filtresi.
  //   Backend SQL GROUP BY yapar; yalnızca ilk N ürün + toplamlar gelir.
  const screenTopAbortRef = React.useRef<AbortController | null>(null);
  const loadScreenTopSelling = async (screens: SelectedScreen[], filter: typeof dateFilter) => {
    if (screens.length === 0) {
      setScreenTopProducts([]);
      setScreenTopTotals({ totalAmount: 0, totalQuantity: 0 });
      return;
    }
    try {
      if (screenTopAbortRef.current) screenTopAbortRef.current.abort();
      const abortController = new AbortController();
      screenTopAbortRef.current = abortController;

      setLoadingScreenTop(true);
      const { startDate, endDate } = getDateRange(filter);

      const result = await salesService.getScreenTopSellingProducts(
        startDate,
        endDate,
        screens.map((s) => s.id),
        11
      );

      if (abortController.signal.aborted) return;

      setScreenTopProducts(result.data);
      setScreenTopTotals({ totalAmount: result.totalAmount, totalQuantity: result.totalQuantity });
      setLoadingScreenTop(false);
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') return;
      console.error('❌ [DASHBOARD] Ekran bazlı en çok satanlar yüklenemedi:', error);
      setScreenTopProducts([]);
      setScreenTopTotals({ totalAmount: 0, totalQuantity: 0 });
      setLoadingScreenTop(false);
    }
  };

  // ✅ YENİ: Restoran ekranı bazlı en çok satanlar — seçili restoran ekranları + aktif tarih filtresi.
  const restaurantScreenTopAbortRef = React.useRef<AbortController | null>(null);
  const loadRestaurantScreenTopSelling = async (screens: SelectedScreen[], filter: typeof dateFilter) => {
    if (screens.length === 0) {
      setRestaurantScreenTopProducts([]);
      setRestaurantScreenTopTotals({ totalAmount: 0, totalQuantity: 0 });
      return;
    }
    try {
      if (restaurantScreenTopAbortRef.current) restaurantScreenTopAbortRef.current.abort();
      const abortController = new AbortController();
      restaurantScreenTopAbortRef.current = abortController;

      setLoadingRestaurantScreenTop(true);
      const { startDate, endDate } = getDateRange(filter);

      const result = await salesService.getRestaurantScreenTopSellingProducts(
        startDate,
        endDate,
        screens.map((s) => s.id),
        11
      );

      if (abortController.signal.aborted) return;

      setRestaurantScreenTopProducts(result.data);
      setRestaurantScreenTopTotals({ totalAmount: result.totalAmount, totalQuantity: result.totalQuantity });
      setLoadingRestaurantScreenTop(false);
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') return;
      console.error('❌ [DASHBOARD] Restoran ekranı bazlı en çok satanlar yüklenemedi:', error);
      setRestaurantScreenTopProducts([]);
      setRestaurantScreenTopTotals({ totalAmount: 0, totalQuantity: 0 });
      setLoadingRestaurantScreenTop(false);
    }
  };

  // ✅ YENİ: Paket sipariş ekranı bazlı en çok satanlar — seçili paket ekranları + aktif tarih filtresi.
  const packageScreenTopAbortRef = React.useRef<AbortController | null>(null);
  const loadPackageScreenTopSelling = async (screens: SelectedScreen[], filter: typeof dateFilter) => {
    if (screens.length === 0) {
      setPackageScreenTopProducts([]);
      setPackageScreenTopTotals({ totalAmount: 0, totalQuantity: 0 });
      return;
    }
    try {
      if (packageScreenTopAbortRef.current) packageScreenTopAbortRef.current.abort();
      const abortController = new AbortController();
      packageScreenTopAbortRef.current = abortController;

      setLoadingPackageScreenTop(true);
      const { startDate, endDate } = getDateRange(filter);

      const result = await salesService.getPackageScreenTopSellingProducts(
        startDate,
        endDate,
        screens.map((s) => s.id),
        11
      );

      if (abortController.signal.aborted) return;

      setPackageScreenTopProducts(result.data);
      setPackageScreenTopTotals({ totalAmount: result.totalAmount, totalQuantity: result.totalQuantity });
      setLoadingPackageScreenTop(false);
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') return;
      console.error('❌ [DASHBOARD] Paket ekranı bazlı en çok satanlar yüklenemedi:', error);
      setPackageScreenTopProducts([]);
      setPackageScreenTopTotals({ totalAmount: 0, totalQuantity: 0 });
      setLoadingPackageScreenTop(false);
    }
  };

  // Ekran bazlı 3'lü kart takımlarından en az biri görünür mü?
  const screenTrioVisible = ['statsScreenTopSelling', 'widgetScreenTopSelling', 'chartScreenTopSelling']
    .some((id) => !hiddenCards.includes(id));
  const restaurantTrioVisible = ['statsRestaurantScreenTopSelling', 'widgetRestaurantScreenTopSelling', 'chartRestaurantScreenTopSelling']
    .some((id) => !hiddenCards.includes(id));
  const packageTrioVisible = ['statsPackageScreenTopSelling', 'widgetPackageScreenTopSelling', 'chartPackageScreenTopSelling']
    .some((id) => !hiddenCards.includes(id));

  // Kartlar görünür + seçim varsa veriyi yükle (tarih filtresi/seçim değişiminde yenile)
  useEffect(() => {
    if (!screenTrioVisible || !userDetails?.companyId) return;
    if (dateFilter === 'custom' && (!customStartDate || !customEndDate)) return;
    loadScreenTopSelling(selectedSaleScreens, dateFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenTrioVisible, selectedSaleScreens, dateFilter, userDetails?.companyId]);

  useEffect(() => {
    if (!restaurantTrioVisible || !userDetails?.companyId) return;
    if (dateFilter === 'custom' && (!customStartDate || !customEndDate)) return;
    loadRestaurantScreenTopSelling(selectedRestaurantScreens, dateFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantTrioVisible, selectedRestaurantScreens, dateFilter, userDetails?.companyId]);

  useEffect(() => {
    if (!packageTrioVisible || !userDetails?.companyId) return;
    if (dateFilter === 'custom' && (!customStartDate || !customEndDate)) return;
    loadPackageScreenTopSelling(selectedPackageScreens, dateFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageTrioVisible, selectedPackageScreens, dateFilter, userDetails?.companyId]);

  // Kartlar görünür olduğunda seçim boşsa ekran seçim dialogunu otomatik aç
  useEffect(() => {
    if (screenTrioVisible && selectedSaleScreens.length === 0) {
      setScreenSelectOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenTrioVisible]);

  useEffect(() => {
    if (restaurantTrioVisible && selectedRestaurantScreens.length === 0) {
      setRestaurantScreenSelectOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantTrioVisible]);

  useEffect(() => {
    if (packageTrioVisible && selectedPackageScreens.length === 0) {
      setPackageScreenSelectOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageTrioVisible]);

  const handleScreenSelectionSave = (screens: SelectedScreen[]) => {
    setSelectedSaleScreens(screens);
    saveScreenSelection('quickSale', screens);
  };

  const handleRestaurantScreenSelectionSave = (screens: SelectedScreen[]) => {
    setSelectedRestaurantScreens(screens);
    saveScreenSelection('restaurant', screens);
  };

  const handlePackageScreenSelectionSave = (screens: SelectedScreen[]) => {
    setSelectedPackageScreens(screens);
    saveScreenSelection('package', screens);
  };

  // Kart başlıklarında seçim etiketi: tek ekran → adı, çoklu → "N ekran"
  const getScreenSelectionLabel = (screens: SelectedScreen[]) =>
    screens.length === 1
      ? screens[0].name
      : screens.length > 1
        ? t('dashboard.screenTopSelling.screenCount', { count: screens.length })
        : '';

  const screenSelectionLabel = getScreenSelectionLabel(selectedSaleScreens);
  const restaurantScreenSelectionLabel = getScreenSelectionLabel(selectedRestaurantScreens);
  const packageScreenSelectionLabel = getScreenSelectionLabel(selectedPackageScreens);

  // Widget/grafik bileşenleri ProductSales formatı bekler (mevcut en çok satanlar seti ile aynı)
  const toProductSales = (products: ScreenTopSellingProduct[]): ProductSales[] =>
    products.map((p) => ({
      itemId: p.itemName,
      itemName: p.itemName,
      totalSales: p.totalQuantity,
    }));

  const screenTopProductsAsSales = toProductSales(screenTopProducts);
  const restaurantScreenTopProductsAsSales = toProductSales(restaurantScreenTopProducts);
  const packageScreenTopProductsAsSales = toProductSales(packageScreenTopProducts);

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };

  // Para birimini formatlayan yardımcı fonksiyon
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Döviz kurunu formatlayan yardımcı fonksiyon
  const formatRate = (rate: number | null): string => {
    if (rate === null) return '---';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    }).format(rate);
  };

  // ✅ YENİ: Dashboard kartlarının render haritası
  // Yeni kart eklerken: dashboardCardsConfig.ts'e kayıt + buraya bir case eklemek yeterli.
  const renderDashboardCard = (cardId: string): React.ReactNode => {
    switch (cardId) {
      // ===== İSTATİSTİK KARTLARI =====
      case 'statsSales':
        return (
          <StatsCard bgcolor={THEME_COLORS.cari} sx={{ height: '100%' }}>
            <IconBox bgcolor={THEME_COLORS.cari}>
              <ReceiptLongIcon />
            </IconBox>
            <StatsText>
              <Typography variant="h6" fontWeight="bold">
                {loadingBranchSales ? <CircularProgress size={20} /> : formatCurrency(branchSummaries.reduce((sum, b) => sum + b.totalBalance, 0))}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {dateFilter === 'daily' ? t('dashboard.todayTotalSales')
                  : dateFilter === 'yesterday' ? t('dashboard.yesterdayTotalSales')
                  : dateFilter === 'weekly' ? t('dashboard.weeklyTotalSales')
                  : dateFilter === 'monthly' ? t('dashboard.monthlyTotalSales')
                  : dateFilter === 'yearly' ? t('dashboard.yearlyTotalSales')
                  : t('dashboard.selectedRangeSalesTotal')}
              </Typography>
            </StatsText>
          </StatsCard>
        );
      case 'statsStock':
        return (
          <StatsCard bgcolor={THEME_COLORS.stok} sx={{ height: '100%' }}>
            <IconBox bgcolor={THEME_COLORS.stok}>
              <InventoryIcon />
            </IconBox>
            <StatsText>
              <Typography variant="h6" fontWeight="bold">
                {loadingInventory ? <CircularProgress size={20} /> : inventoryItemCount}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('dashboard.totalStockItems')}
              </Typography>
            </StatsText>
          </StatsCard>
        );
      case 'statsCashbox':
        return (
          <StatsCard bgcolor={THEME_COLORS.kasa} sx={{ height: '100%' }}>
            <IconBox bgcolor={THEME_COLORS.kasa}>
              <LocalAtmIcon />
            </IconBox>
            <StatsText>
              <Typography variant="h6" fontWeight="bold">
                {loadingCashboxBalance ? <CircularProgress size={20} /> : formatCurrency(totalCashboxBalance)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('dashboard.totalCashBalance')}
              </Typography>
            </StatsText>
          </StatsCard>
        );
      case 'statsBank':
        return (
          <StatsCard bgcolor={THEME_COLORS.banka} sx={{ height: '100%' }}>
            <IconBox bgcolor={THEME_COLORS.banka}>
              <AccountBalanceIcon />
            </IconBox>
            <StatsText>
              <Typography variant="h6" fontWeight="bold">
                {loadingBankBalance ? <CircularProgress size={20} /> : formatCurrency(totalBankBalance)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('dashboard.totalBankBalance')}
              </Typography>
            </StatsText>
          </StatsCard>
        );
      case 'statsScreenTopSelling':
        return (
          <StatsCard bgcolor={THEME_COLORS.doviz} sx={{ height: '100%' }}>
            <IconBox bgcolor={THEME_COLORS.doviz}>
              <PointOfSaleIcon />
            </IconBox>
            <StatsText>
              {selectedSaleScreens.length === 0 ? (
                <>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem !important' }}>
                    {t('dashboard.screenTopSelling.noScreenSelected')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('dashboard.screenTopSelling.statsTitle')}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" fontWeight="bold">
                    {loadingScreenTop ? <CircularProgress size={20} /> : formatCurrency(screenTopTotals.totalAmount)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('dashboard.screenTopSelling.statsTitle')}
                    {screenSelectionLabel ? ` • ${screenSelectionLabel}` : ''}
                  </Typography>
                </>
              )}
            </StatsText>
            <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setScreenSelectOpen(true);
                }}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </StatsCard>
        );
      case 'statsRestaurantScreenTopSelling':
        return (
          <StatsCard bgcolor={THEME_COLORS.depo} sx={{ height: '100%' }}>
            <IconBox bgcolor={THEME_COLORS.depo}>
              <TableRestaurantIcon />
            </IconBox>
            <StatsText>
              {selectedRestaurantScreens.length === 0 ? (
                <>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem !important' }}>
                    {t('dashboard.screenTopSelling.noScreenSelected')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('dashboard.screenTopSelling.restaurantStatsTitle')}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" fontWeight="bold">
                    {loadingRestaurantScreenTop ? <CircularProgress size={20} /> : formatCurrency(restaurantScreenTopTotals.totalAmount)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('dashboard.screenTopSelling.restaurantStatsTitle')}
                    {restaurantScreenSelectionLabel ? ` • ${restaurantScreenSelectionLabel}` : ''}
                  </Typography>
                </>
              )}
            </StatsText>
            <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setRestaurantScreenSelectOpen(true);
                }}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </StatsCard>
        );
      case 'statsPackageScreenTopSelling':
        return (
          <StatsCard bgcolor={THEME_COLORS.cari} sx={{ height: '100%' }}>
            <IconBox bgcolor={THEME_COLORS.cari}>
              <DeliveryDiningIcon />
            </IconBox>
            <StatsText>
              {selectedPackageScreens.length === 0 ? (
                <>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem !important' }}>
                    {t('dashboard.screenTopSelling.noScreenSelected')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('dashboard.screenTopSelling.packageStatsTitle')}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" fontWeight="bold">
                    {loadingPackageScreenTop ? <CircularProgress size={20} /> : formatCurrency(packageScreenTopTotals.totalAmount)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('dashboard.screenTopSelling.packageStatsTitle')}
                    {packageScreenSelectionLabel ? ` • ${packageScreenSelectionLabel}` : ''}
                  </Typography>
                </>
              )}
            </StatsText>
            <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setPackageScreenSelectOpen(true);
                }}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </StatsCard>
        );

      // ===== WIDGET'LAR =====
      case 'widgetBranchSales':
        return (
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <Box sx={{
              p: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {dateFilter === 'daily' ? t('dashboard.todaySalesByBranch')
                  : dateFilter === 'yesterday' ? t('dashboard.yesterdaySalesByBranch')
                  : dateFilter === 'weekly' ? t('dashboard.weeklySalesByBranch')
                  : dateFilter === 'monthly' ? t('dashboard.monthlySalesByBranch')
                  : dateFilter === 'yearly' ? t('dashboard.yearlySalesByBranch')
                  : t('dashboard.selectedRangeSalesByBranch')}
              </Typography>
              {/* Yönetici tek şubeye kilitli olduğu için tüm şubeler görünümü ona kapalıdır. */}
              {!isManager && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAllBranchesOpen(true);
                  }}
                  sx={{ textTransform: 'none', minWidth: 'auto', flexShrink: 0, py: 0.25 }}
                >
                  {t('dashboard.viewAll')}
                </Button>
              )}
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {loadingBranchSales || !branches || branches.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : branchSummaries.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('dashboard.noSalesRecordForRange')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ height: '100%' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper', width: 40, px: 1 }}>
                          {t('dashboard.rowNumber')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>
                          {t('dashboard.branchColumn')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>
                          {t('dashboard.amountColumn')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branchSummaries.map((summary, index) => {
                        const branchName = summary.branchId
                          ? ((branches || []).find((b: any) => (b.id || b.branchId) === summary.branchId)?.name || summary.branchId)
                          : t('dashboard.allBranchesOverall');
                        const branchNo = branchDisplayNo(branchSummaries, index);
                        return (
                          <TableRow
                            key={summary.branchId || index}
                            hover
                            onClick={() => handleBranchDetailOpen(summary, branchName)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ width: 40, px: 1, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                              {branchNo ?? ''}
                            </TableCell>
                            <TableCell sx={{ py: 0.75, maxWidth: 140 }}>
                              <Typography variant="body2" fontWeight="bold" noWrap>
                                {branchName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {t('dashboard.salesMovementsCount', { count: summary.transactionCount })}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main', whiteSpace: 'nowrap' }}>
                              {formatCurrency(summary.totalBalance)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        );
      case 'widgetBestSelling':
        return (
          <BestSellingProductsWidget
            products={bestSellingProducts}
            loading={loadingBestSelling}
            onDetailClick={() => setTopSellingModalOpen(true)}
          />
        );
      case 'widgetCashboxes':
        return (
          <CashboxListWidget
            cashboxes={cashboxes}
            loading={loadingCashboxes}
            getCurrencySymbol={getCurrencySymbol}
            branches={branches || []}
          />
        );
      case 'widgetBanks':
        return (
          <BankAccountListWidget
            bankAccounts={bankAccounts}
            loading={loadingBankAccounts}
            getCurrencySymbol={getCurrencySymbol}
            branches={branches || []}
          />
        );
      case 'widgetScreenTopSelling':
        return (
          <BestSellingProductsWidget
            products={screenTopProductsAsSales}
            loading={loadingScreenTop}
            title={screenSelectionLabel
              ? `${t('dashboard.screenTopSelling.widgetTitle')} • ${screenSelectionLabel}`
              : t('dashboard.screenTopSelling.widgetTitle')}
            headerAction={
              <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
                <IconButton size="small" onClick={() => setScreenSelectOpen(true)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        );
      case 'widgetRestaurantScreenTopSelling':
        return (
          <BestSellingProductsWidget
            products={restaurantScreenTopProductsAsSales}
            loading={loadingRestaurantScreenTop}
            title={restaurantScreenSelectionLabel
              ? `${t('dashboard.screenTopSelling.restaurantWidgetTitle')} • ${restaurantScreenSelectionLabel}`
              : t('dashboard.screenTopSelling.restaurantWidgetTitle')}
            headerAction={
              <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
                <IconButton size="small" onClick={() => setRestaurantScreenSelectOpen(true)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        );
      case 'widgetPackageScreenTopSelling':
        return (
          <BestSellingProductsWidget
            products={packageScreenTopProductsAsSales}
            loading={loadingPackageScreenTop}
            title={packageScreenSelectionLabel
              ? `${t('dashboard.screenTopSelling.packageWidgetTitle')} • ${packageScreenSelectionLabel}`
              : t('dashboard.screenTopSelling.packageWidgetTitle')}
            headerAction={
              <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
                <IconButton size="small" onClick={() => setPackageScreenSelectOpen(true)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        );

      // ===== GRAFİKLER =====
      case 'chartSalesDistribution':
        return (
          <SalesDistributionChart
            data={branchSummaries}
            loading={loadingBranchSales}
            branches={branches || []}
          />
        );
      case 'chartBestSelling':
        return (
          <BestSellingProductsChart
            data={bestSellingProducts}
            loading={loadingBestSelling}
          />
        );
      case 'chartCashboxBalance':
        return (
          <CashboxBalanceChart
            data={cashboxes}
            loading={loadingCashboxes}
            getCurrencySymbol={getCurrencySymbol}
          />
        );
      case 'chartBankBalance':
        return (
          <BankBalanceChart
            data={bankAccounts}
            loading={loadingBankAccounts}
            getCurrencySymbol={getCurrencySymbol}
          />
        );
      case 'chartScreenTopSelling':
        return (
          <BestSellingProductsChart
            data={screenTopProductsAsSales}
            loading={loadingScreenTop}
            title={screenSelectionLabel
              ? `${t('dashboard.screenTopSelling.chartTitle')} • ${screenSelectionLabel}`
              : t('dashboard.screenTopSelling.chartTitle')}
            headerAction={
              <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
                <IconButton size="small" onClick={() => setScreenSelectOpen(true)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        );
      case 'chartRestaurantScreenTopSelling':
        return (
          <BestSellingProductsChart
            data={restaurantScreenTopProductsAsSales}
            loading={loadingRestaurantScreenTop}
            title={restaurantScreenSelectionLabel
              ? `${t('dashboard.screenTopSelling.restaurantChartTitle')} • ${restaurantScreenSelectionLabel}`
              : t('dashboard.screenTopSelling.restaurantChartTitle')}
            headerAction={
              <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
                <IconButton size="small" onClick={() => setRestaurantScreenSelectOpen(true)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        );
      case 'chartPackageScreenTopSelling':
        return (
          <BestSellingProductsChart
            data={packageScreenTopProductsAsSales}
            loading={loadingPackageScreenTop}
            title={packageScreenSelectionLabel
              ? `${t('dashboard.screenTopSelling.packageChartTitle')} • ${packageScreenSelectionLabel}`
              : t('dashboard.screenTopSelling.packageChartTitle')}
            headerAction={
              <Tooltip title={t('dashboard.screenTopSelling.selectScreens')}>
                <IconButton size="small" onClick={() => setPackageScreenSelectOpen(true)}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        );
      default:
        // ✅ YENİ: custom_ önekli id'ler kullanıcı tanımlı dinamik rapor kartlarıdır
        if (cardId.startsWith(CUSTOM_REPORT_ID_PREFIX)) {
          const spec = customReports.find((r) => r.id === cardId);
          if (!spec) return null;
          return (
            <DynamicReportCard
              spec={spec}
              startDate={dynamicReportRange.startDate}
              endDate={dynamicReportRange.endDate}
              branches={branches || []}
              onEdit={handleEditCustomReport}
              onDelete={handleDeleteCustomReport}
            />
          );
        }
        return null;
    }
  };

  return (
    <Box ref={rootRef} sx={{ pb: 4, position: 'relative' }}>
      {/* ✅ Aşağı çekerek yenileme göstergesi */}
      {(pullDistance > 0 || refreshing) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 1200,
            transform: `translateY(${refreshing ? 12 : Math.max(pullDistance - 28, -8)}px)`,
            transition: isPulling ? 'none' : 'transform 0.2s ease',
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '50%',
              boxShadow: 3,
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              size={26}
              variant={refreshing ? 'indeterminate' : 'determinate'}
              value={refreshing ? undefined : Math.min((pullDistance / PULL_THRESHOLD) * 100, 100)}
            />
          </Box>
        </Box>
      )}
      {/* Widget Yönetim Butonu */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 2, md: 0 },
        mb: 2,
        backgroundColor: 'background.paper',
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 0.5 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            {t('dashboard.title')}
          </Typography>
          <GuideButton guideKey="finance.adminDashboard" />
          <Tooltip title="Verileri yenile">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={triggerRefresh}
                disabled={refreshing}
                aria-label="refresh"
                startIcon={
                  <RefreshIcon
                    fontSize="small"
                    sx={{ animation: refreshing ? 'dashboardSpin 0.9s linear infinite' : 'none', '@keyframes dashboardSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }}
                  />
                }
                sx={{ ml: 0.5, minWidth: 'auto' }}
              >
                {refreshing ? 'Yenileniyor…' : 'Yenile'}
              </Button>
            </span>
          </Tooltip>
        </Box>
        
        {/* ✅ Şube + Tarih Filtresi */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center', 
          gap: 2 
        }}>
          {/* ✅ Şube Filtresi Autocomplete */}
          <Autocomplete
            size="small"
            disabled={isManager}
            options={branches || []}
            getOptionLabel={(option: any) => option.name || option.branchName || ''}
            value={(branches || []).find((b: any) => (b.id || b.branchId) === selectedBranchId) || null}
            onChange={(_event: any, newValue: any) => {
              setSelectedBranchId(newValue ? (newValue.id || newValue.branchId) : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={isManager ? t('dashboard.lockedBranch') : t('dashboard.branchFilter')}
                placeholder={isManager ? '' : t('dashboard.allBranches')}
              />
            )}
            sx={{ minWidth: 200, width: { xs: '100%', md: 200 } }}
            noOptionsText={t('dashboard.noBranchFound')}
          />
          {/* ✅ Özel Tarih Aralığı Toggle Butonu */}
          <Tooltip title={isCustomDateActive ? t('dashboard.clearCustomDateFilter') : t('dashboard.selectCustomDateRange')}>
            <Button
              variant={isCustomDateActive ? 'contained' : 'outlined'}
              color={isCustomDateActive ? 'secondary' : 'primary'}
              size="small"
              onClick={() => {
                if (isCustomDateActive) {
                  setIsCustomDateActive(false);
                  setDateFilter('daily');
                } else {
                  setIsDateRangeModalOpen(true);
                }
              }}
              startIcon={<DateRangeIcon />}
              sx={{ 
                textTransform: 'none', 
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                ...(isCustomDateActive && {
                  animation: 'none',
                  fontWeight: 'bold',
                })
              }}
            >
              {isCustomDateActive && customStartDate && customEndDate 
                ? `${formatDateForDisplay(customStartDate)} - ${formatDateForDisplay(customEndDate)} (${getCustomDateRangeLabel()})`
                : t('dashboard.dateRange')
              }
            </Button>
          </Tooltip>

          <ButtonGroup 
            variant="outlined" 
            size="small" 
            fullWidth
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <Button
              variant={!isCustomDateActive && dateFilter === 'daily' ? 'contained' : 'outlined'}
              onClick={() => {
                setIsCustomDateActive(false);
                setDateFilter('daily');
              }}
              sx={{ textTransform: 'none', flex: 1 }}
            >
              {t('dashboard.daily')}
            </Button>
            <Button
              variant={!isCustomDateActive && dateFilter === 'yesterday' ? 'contained' : 'outlined'}
              onClick={() => {
                setIsCustomDateActive(false);
                setDateFilter('yesterday');
              }}
              sx={{ textTransform: 'none', flex: 1 }}
            >
              {t('dashboard.yesterday')}
            </Button>
            <Button
              variant={!isCustomDateActive && dateFilter === 'weekly' ? 'contained' : 'outlined'}
              onClick={() => {
                setIsCustomDateActive(false);
                setDateFilter('weekly');
              }}
              sx={{ textTransform: 'none', flex: 1 }}
            >
              {t('dashboard.weekly')}
            </Button>
            <Button
              variant={!isCustomDateActive && dateFilter === 'monthly' ? 'contained' : 'outlined'}
              onClick={() => {
                setIsCustomDateActive(false);
                setDateFilter('monthly');
              }}
              sx={{ textTransform: 'none', flex: 1 }}
            >
              {t('dashboard.monthly')}
            </Button>
            <Button
              variant={!isCustomDateActive && dateFilter === 'yearly' ? 'contained' : 'outlined'}
              onClick={() => {
                setIsCustomDateActive(false);
                setDateFilter('yearly');
              }}
              sx={{ textTransform: 'none', flex: 1 }}
            >
              {t('dashboard.yearly')}
            </Button>
          </ButtonGroup>
          
          {/* ✅ Dashboard Ayarları (çark) — Düzen + Görünüm menüsü */}
          <Tooltip title={t('dashboard.dashboardSettings')}>
            <IconButton
              size="small"
              onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
              sx={{
                border: '1px solid',
                borderColor: isLayoutEditMode ? 'primary.main' : 'divider',
                borderRadius: 2,
                color: isLayoutEditMode ? 'primary.main' : 'text.secondary',
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={settingsMenuAnchor}
            open={Boolean(settingsMenuAnchor)}
            onClose={() => setSettingsMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={handleEnterLayoutEdit}
              disabled={isMobileLayout || isLayoutEditMode}
            >
              <ListItemIcon>
                <OpenWithIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('dashboard.layoutEditMenuItem')}
                secondary={isMobileLayout ? t('dashboard.layoutEditDesktopOnly') : undefined}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setViewSettingsOpen(true);
                setSettingsMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <TuneIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('dashboard.viewSettingsMenuItem')} />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ✅ Özel Tarih Aralığı Seçim Modalı */}
      <Dialog
        open={isDateRangeModalOpen}
        onClose={() => setIsDateRangeModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateRangeIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">{t('dashboard.dateRangeSelectTitle')}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsDateRangeModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
              <DateTimePicker
                label={t('dashboard.startDate')}
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                format="dd.MM.yyyy HH:mm"
                ampm={false}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' }
                }}
              />
              <DateTimePicker
                label={t('dashboard.endDate')}
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                format="dd.MM.yyyy HH:mm"
                ampm={false}
                minDateTime={customStartDate || undefined}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' }
                }}
              />
            </Box>
          </LocalizationProvider>

          {/* Seçilen aralık bilgisi */}
          {customStartDate && customEndDate && customEndDate > customStartDate && (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: 'action.hover',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('dashboard.dateRangeInfo')}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                {formatDateForDisplay(customStartDate)} — {formatDateForDisplay(customEndDate)}
              </Typography>
              <Chip 
                label={getCustomDateRangeLabel()} 
                color="primary" 
                variant="outlined" 
                size="small" 
                sx={{ mt: 1 }} 
              />
            </Paper>
          )}

          {customStartDate && customEndDate && customEndDate <= customStartDate && (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: 'error.light',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" color="error.contrastText">
                {t('dashboard.endDateValidation')}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setIsDateRangeModalOpen(false)}
            color="inherit"
          >
            {t('dashboard.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (customStartDate && customEndDate && customEndDate > customStartDate) {
                setIsCustomDateActive(true);
                setDateFilter('custom');
                setIsDateRangeModalOpen(false);
              }
            }}
            disabled={!customStartDate || !customEndDate || (customEndDate !== null && customStartDate !== null && customEndDate <= customStartDate)}
            startIcon={<DateRangeIcon />}
          >
            {t('dashboard.filter')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Düzenleme modu bilgi şeridi */}
      {isLayoutEditMode && (
        <Alert
          severity="info"
          icon={<OpenWithIcon />}
          sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
          action={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                color="inherit"
                startIcon={<RestartAltIcon />}
                onClick={handleResetPendingLayout}
                sx={{ textTransform: 'none' }}
              >
                {t('dashboard.layoutResetToDefault')}
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={handleCancelLayoutEdit}
                sx={{ textTransform: 'none' }}
              >
                {t('dashboard.layoutCancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveLayoutEdit}
                sx={{ textTransform: 'none' }}
              >
                {t('dashboard.layoutSave')}
              </Button>
            </Box>
          }
        >
          {t('dashboard.layoutEditModeInfo')}
        </Alert>
      )}

      {/* ✅ Dashboard Kartları - Özelleştirilebilir Grid Düzeni */}
      <Box
        ref={gridWrapperRef}
        sx={{
          mb: 2,
          // Boyutlandırma tutamacı overlay'in üstünde kalsın
          '& .react-grid-item > .react-resizable-handle': { zIndex: 20 },
          '& .react-grid-item.react-grid-placeholder': {
            backgroundColor: 'primary.main',
            opacity: 0.15,
            borderRadius: 2,
          },
        }}
      >
        {isMobileLayout ? (
          // 📱 Mobil: kartlar mobil okuma sırasına göre alt alta (düzenleme kapalı)
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {mobileCardIdsOrdered.map((cardId) => {
              const cardDef = DASHBOARD_CARDS.find((c) => c.id === cardId);
              // Özel rapor kartları: KPI kısa, diğer görseller grafik yüksekliğinde
              const customSpec = cardId.startsWith(CUSTOM_REPORT_ID_PREFIX)
                ? customReports.find((r) => r.id === cardId)
                : undefined;
              // Widget/grafik kartlarına mobilde sabit yükseklik ver (eski maxHeight davranışı)
              const mobileHeight = customSpec
                ? (customSpec.visual === 'kpi' ? 'auto' : 360)
                : cardDef?.group === 'widget' ? 430 : cardDef?.group === 'chart' ? 360 : 'auto';
              return (
                <Box key={cardId} sx={{ height: mobileHeight }}>
                  {renderDashboardCard(cardId)}
                </Box>
              );
            })}
          </Box>
        ) : (
          <GridLayout
            className="dashboard-grid-layout"
            layout={activeLayout}
            cols={DASHBOARD_GRID_COLS}
            rowHeight={DASHBOARD_ROW_HEIGHT}
            width={gridWidth}
            margin={DASHBOARD_GRID_MARGIN}
            containerPadding={[0, 0]}
            isDraggable={isLayoutEditMode}
            isResizable={isLayoutEditMode}
            compactType="vertical"
            preventCollision={false}
            isBounded={true}
            useCSSTransforms={true}
            onLayoutChange={(newLayout: Layout[]) => {
              if (!isLayoutEditMode) return;
              // Taslağa uygula (gizli kartların kayıtları korunur)
              setPendingLayout((prev) => {
                const byId = new Map(newLayout.map((l) => [l.i, l]));
                return prev.map((item) => {
                  const updated = byId.get(item.i);
                  return updated
                    ? { ...item, x: updated.x, y: updated.y, w: updated.w, h: updated.h }
                    : item;
                });
              });
            }}
          >
            {activeLayout.map((item) => (
              <div key={item.i} style={{ height: '100%' }}>
                <Box
                  sx={{
                    height: '100%',
                    position: 'relative',
                    ...(isLayoutEditMode && {
                      outline: '2px dashed',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                      borderRadius: 2,
                    }),
                  }}
                >
                  {renderDashboardCard(item.i)}
                  {isLayoutEditMode && (
                    // Düzenleme modunda kart içi tıklamaları engelleyen şeffaf katman
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 10,
                        cursor: 'move',
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                      }}
                    />
                  )}
                </Box>
              </div>
            ))}
          </GridLayout>
        )}
      </Box>

      {/* Tüm Şubeler Modalı — dönem içinde satışı olmayan şubeler de 0 ile listelenir */}
      <Dialog
        open={allBranchesOpen}
        onClose={() => setAllBranchesOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
              {t('dashboard.allBranchesTitle')}
            </Typography>
            <Typography
              variant="h4"
              fontWeight="bold"
              color="primary.main"
              sx={{ mt: 0.5, lineHeight: 1.2 }}
            >
              {formatCurrency(allBranchRows.reduce((sum, row) => sum + row.totalBalance, 0))}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.25 }}>
              {t('dashboard.allBranchesCount', { count: allBranchRows.length })}
            </Typography>
          </Box>
          <IconButton onClick={() => setAllBranchesOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {allBranchesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={30} />
            </Box>
          ) : allBranchRows.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">{t('dashboard.noBranchFound')}</Typography>
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: 48, px: 1 }}>{t('dashboard.rowNumber')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('dashboard.branchColumn')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('dashboard.amountColumn')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allBranchRows.map((row, index) => {
                  const no = branchDisplayNo(allBranchRows, index);
                  return (
                    <TableRow
                      key={row.branchId || `no-branch-${index}`}
                      hover
                      onClick={() => handleBranchDetailOpen(
                        { branchId: row.branchId, totalBalance: row.totalBalance, transactionCount: row.transactionCount },
                        row.branchName
                      )}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ width: 48, px: 1, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                        {no ?? ''}
                      </TableCell>
                      <TableCell sx={{ py: 1, maxWidth: 260 }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {row.branchName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('dashboard.salesMovementsCount', { count: row.transactionCount })}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          color: row.totalBalance > 0 ? 'success.main' : 'text.disabled'
                        }}
                      >
                        {formatCurrency(row.totalBalance)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Şube Satış Detay Modalı */}
      <Dialog
        open={branchDetailOpen}
        onClose={() => setBranchDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
              {branchDetailBranchName}
            </Typography>
            <Typography
              variant="h4"
              fontWeight="bold"
              color="primary.main"
              sx={{ mt: 0.5, lineHeight: 1.2 }}
            >
              {formatCurrency(branchDetailTotalAmount)}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.25 }}>
              {t('dashboard.branchSalesDetailGroupedSummary', {
                count: branchDetailTotalCount,
                quantity: branchDetailTotalQuantity.toLocaleString(locale, { maximumFractionDigits: 3 })
              })}
            </Typography>
          </Box>
          <IconButton onClick={() => setBranchDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {branchDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={30} />
            </Box>
          ) : branchDetailData.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">{t('dashboard.noSalesMovementFound')}</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: 48, px: 1 }}>{t('dashboard.rowNumber')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('dashboard.productName')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('dashboard.quantity')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('dashboard.avgUnitPrice')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('dashboard.total')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('dashboard.lastSale')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branchDetailData.map((item: any, idx: number) => (
                    <TableRow key={item.ProductName || item.productName || idx} hover>
                      <TableCell sx={{ width: 48, px: 1, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                        {(branchDetailPage - 1) * branchDetailPageSize + idx + 1}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.productName || item.ProductName}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(item.quantity || item.Quantity || 0).toLocaleString(locale, { maximumFractionDigits: 3 })} {item.unit || item.Unit || t('dashboard.piece')}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(parseFloat(item.unitPrice || item.UnitPrice || 0))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(parseFloat(item.Total ?? item.totalAmount ?? item.TotalAmount ?? 0))}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {/* Gruplu satırda Date, ürünün dönemdeki en son satışıdır; zaten Türkiye saatidir. */}
                        {(() => { const d = new Date(item.Date ?? item.createdAt ?? item.CreatedAt); return isNaN(d.getTime()) ? '-' : d.toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="textSecondary">{t('dashboard.rowsPerPage')}</Typography>
            <FormControl size="small" variant="outlined" sx={{ minWidth: 70 }}>
              <Select
                value={branchDetailPageSize}
                onChange={(e) => handleBranchDetailPageSizeChange(Number(e.target.value))}
                sx={{ fontSize: '0.85rem', height: 32 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {branchDetailPage} / {branchDetailTotalPages || 1}
            </Typography>
            <IconButton
              size="small"
              disabled={branchDetailPage <= 1}
              onClick={() => handleBranchDetailPageChange(branchDetailPage - 1)}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              size="small"
              disabled={branchDetailPage >= branchDetailTotalPages}
              onClick={() => handleBranchDetailPageChange(branchDetailPage + 1)}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Görünüm Ayarları - kart aç/kapat */}
      <DashboardSettingsModal
        open={viewSettingsOpen}
        onClose={() => setViewSettingsOpen(false)}
        hiddenCards={hiddenCards}
        onToggleCard={toggleCardVisibility}
        customReports={customReports}
        onAddReport={handleOpenReportBuilder}
        onEditReport={handleEditCustomReport}
        onDeleteReport={handleDeleteCustomReport}
        onAskAi={aiReportEnabled ? handleOpenAiReport : undefined}
      />

      {/* ✅ YENİ: Dinamik rapor kartı sihirbazı (oluşturma + düzenleme) */}
      <ReportBuilderDialog
        open={reportBuilderOpen}
        onClose={() => {
          setReportBuilderOpen(false);
          setEditingReport(null);
        }}
        onSave={handleSaveCustomReport}
        branches={branches || []}
        startDate={dynamicReportRange.startDate}
        endDate={dynamicReportRange.endDate}
        initialSpec={editingReport}
      />

      {/* ✅ YENİ: AI rapor asistanı (doğal dil → rapor kartı önerisi) */}
      <AiReportDialog
        open={aiReportOpen}
        onClose={() => setAiReportOpen(false)}
        onSave={handleSaveCustomReport}
        onEditInBuilder={handleEditAiSuggestionInBuilder}
        branches={branches || []}
        startDate={dynamicReportRange.startDate}
        endDate={dynamicReportRange.endDate}
      />

      {/* ✅ YENİ: Ekran bazlı kart takımı için hızlı satış ekranı seçimi */}
      <ScreenSelectDialog
        open={screenSelectOpen}
        onClose={() => setScreenSelectOpen(false)}
        mode="quickSale"
        selectedIds={selectedSaleScreens.map((s) => s.id)}
        onSave={handleScreenSelectionSave}
        branches={branches || []}
      />

      {/* ✅ YENİ: Restoran kart takımı için restoran ekranı seçimi */}
      <ScreenSelectDialog
        open={restaurantScreenSelectOpen}
        onClose={() => setRestaurantScreenSelectOpen(false)}
        mode="restaurant"
        selectedIds={selectedRestaurantScreens.map((s) => s.id)}
        onSave={handleRestaurantScreenSelectionSave}
        branches={branches || []}
      />

      {/* ✅ YENİ: Paket kart takımı için paket sipariş ekranı seçimi */}
      <ScreenSelectDialog
        open={packageScreenSelectOpen}
        onClose={() => setPackageScreenSelectOpen(false)}
        mode="package"
        selectedIds={selectedPackageScreens.map((s) => s.id)}
        onSave={handlePackageScreenSelectionSave}
        branches={branches || []}
      />

      {/* ✅ YENİ: En Çok Satan Ürünler - Detay Modalı (server-side pagination, tüm ürünler) */}
      {(() => {
        const { startDate, endDate } = getDateRange(dateFilter);
        return (
          <TopSellingProductsModal
            open={topSellingModalOpen}
            onClose={() => setTopSellingModalOpen(false)}
            startDate={startDate}
            endDate={endDate}
            branchId={selectedBranchId}
          />
        );
      })()}
    </Box>
  );
};

export default AccountingDashboard; 